import type { MouseEvent } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    List,
    Popper,
    Fade,
    Box,
    makeStyles,
    IconButton,
    ClickAwayListener,
    ListItem,
    ListItemText,
} from '@material-ui/core';
import type { PopperPlacementType } from '@material-ui/core/Popper';

import { showToast } from '@klpkonsern/ui';
import { Block } from '@klp/design/Block';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { Typography } from '@klp/design/Typography';
import { ParamType, getFromStorage } from '@klp/utils';

import { usePostSendSampleMutation } from '../services/rtkApi';

interface PopperProps {
    handleData: {
        messageId: number;
    };
}

export default function TransitionsPopper({ handleData }: PopperProps) {
    const { t } = useTranslation();
    const [isHover, setIsHover] = useState(false);

    const useStyles = makeStyles(() => ({
        popper: {
            background: 'white',
            border: '1px solid',
            borderRadius: 2,
        },
        redText: {
            color: 'red',
        },
        button: {
            border: isHover ? '1px solid' : '',
            borderRadius: '50%',
            cursor: 'pointer',
        },
    }));
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [placement, setPlacement] = useState<PopperPlacementType>();

    const handleMouseEnter = () => {
        setIsHover(true);
    };
    const handleMouseLeave = () => {
        setIsHover(false);
    };

    const handlePopperClick =
        (newPlacement: PopperPlacementType) =>
        (event: MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
            setOpen((prev) => placement !== newPlacement || !prev);
            setPlacement(newPlacement);
            event.stopPropagation();
        };

    const handleClosePopper = () => {
        setOpen(false);
    };

    const canBeOpen = open && Boolean(anchorEl);
    const id = canBeOpen ? 'transition-popper' : undefined;

    const [postSendSample, { isLoading: postSendSampleLoading }] =
        usePostSendSampleMutation();

    const handlePostSendSample = async (
        messageId: number,
        email?: string,
        culture?: string,
    ) => {
        await postSendSample({
            messageId,
            email,
            culture,
        })
            .unwrap()
            .then(() => {
                showToast({
                    body: t('general.successfulOperation'),
                    type: 'success',
                });
            })
            .catch(() => {
                showToast({
                    body: t('general.errorMessage'),
                    type: 'error',
                });
            });
        handleClosePopper();
    };

    return (
        <ClickAwayListener onClickAway={handleClosePopper}>
            <div>
                <IconButton
                    color="inherit"
                    aria-label="Open document view"
                    onClick={handlePopperClick('bottom-start')}
                    className={classes.button}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <Illustration icon={IconName.OPTIONS} size="medium" />
                </IconButton>
                <Popper
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    placement={placement}
                    transition={true}
                >
                    {({ TransitionProps }) => (
                        <Fade {...TransitionProps} timeout={350}>
                            <Box className={classes.popper}>
                                <List>
                                    <ListItem
                                        dense={true}
                                        button={true}
                                        disabled={postSendSampleLoading}
                                    >
                                        <Block
                                            paddingRight="compact"
                                            paddingTop="tiny"
                                        >
                                            <Illustration
                                                icon={IconName.MAIL}
                                            />
                                        </Block>
                                        <ListItemText
                                            onClick={() => {
                                                handlePostSendSample(
                                                    handleData?.messageId,
                                                    undefined,
                                                    getFromStorage(
                                                        ParamType.lang,
                                                    ),
                                                );
                                            }}
                                        >
                                            <Typography>
                                                {t(
                                                    'message.actions.sendCopyToMe',
                                                )}
                                            </Typography>
                                        </ListItemText>
                                    </ListItem>
                                </List>
                            </Box>
                        </Fade>
                    )}
                </Popper>
            </div>
        </ClickAwayListener>
    );
}
